import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendingUp, FileText, Clock, Users, Activity, Calendar, Shield } from 'lucide-react';

const API = `${window.__WR_API_BASE__}/api`;

/**
 * LiveMetricsDashboard — public aggregate platform stats. No auth required.
 * Fed by GET /api/public/momentum-stats which returns total/month/today
 * counts plus minutes-saved estimates.
 *
 * Designed for the landing page or Med-Group leadership briefings.
 * Refreshes every 60s while mounted.
 */
const StatCard = ({ icon: Icon, label, value, sublabel, accent = 'amber' }) => {
  const accentClasses = {
    amber: 'border-amber-500/30 bg-amber-500/5 text-amber-300',
    sky: 'border-sky-500/30 bg-sky-500/5 text-sky-300',
    emerald: 'border-emerald-500/30 bg-emerald-500/5 text-emerald-300',
    rose: 'border-rose-500/30 bg-rose-500/5 text-rose-300',
  };
  return (
    <div
      className={`rounded-lg border p-4 flex flex-col gap-2 ${accentClasses[accent]}`}
      data-testid={`metric-card-${label.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider opacity-80 font-semibold">
        <Icon className="w-3.5 h-3.5" />
        <span>{label}</span>
      </div>
      <div className="text-3xl font-bold tabular-nums leading-none">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      {sublabel && (
        <div className="text-[11px] opacity-70">{sublabel}</div>
      )}
    </div>
  );
};

export const LiveMetricsDashboard = ({ compact = false, onClose }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/public/momentum-stats`);
        if (!cancelled) { setStats(res.data); setError(null); }
      } catch (e) {
        if (!cancelled) setError('Could not load momentum stats');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetch();
    const interval = setInterval(fetch, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  if (loading) {
    return (
      <div className="rounded-lg border border-border bg-card p-6 text-center text-sm text-muted-foreground" data-testid="live-metrics-loading">
        Loading platform momentum…
      </div>
    );
  }
  if (error || !stats) {
    return (
      <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-6 text-center text-sm text-rose-300" data-testid="live-metrics-error">
        {error || 'No stats available'}
      </div>
    );
  }

  const totals = stats.total || {};
  const month = stats.month || {};
  const today = stats.today || {};
  const minutesSaved = stats.total_minutes_saved || 0;
  const hoursSaved = Math.round(minutesSaved / 60);
  const daysSaved = (minutesSaved / 60 / 8).toFixed(1); // 8-hour clinic day

  return (
    <div className={compact ? '' : 'space-y-5'} data-testid="live-metrics-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
            <Activity className="w-5 h-5 text-amber-400" />
            Platform Momentum
          </h2>
          {!compact && (
            <p className="text-xs text-muted-foreground mt-1">
              Live aggregate stats across all WriteRight PCM+ providers · refreshes every 60s
            </p>
          )}
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground text-sm"
            data-testid="live-metrics-close"
          >
            ✕
          </button>
        )}
      </div>

      {/* Top-line cards */}
      <div className={`grid gap-3 ${compact ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4'}`}>
        <StatCard
          icon={FileText}
          label="Notes Generated"
          value={totals.clinical_notes || 0}
          sublabel="all-time"
          accent="amber"
        />
        <StatCard
          icon={Clock}
          label="Minutes Saved"
          value={minutesSaved.toLocaleString()}
          sublabel={`≈ ${hoursSaved.toLocaleString()} hours · ${daysSaved} clinic days`}
          accent="emerald"
        />
        <StatCard
          icon={Calendar}
          label="This Month"
          value={month.sessions || 0}
          sublabel="encounters documented"
          accent="sky"
        />
        <StatCard
          icon={TrendingUp}
          label="Today"
          value={today.sessions || 0}
          sublabel="encounters in the last 24 hours"
          accent="rose"
        />
      </div>

      {!compact && (
        <>
          {/* Document-type breakdown */}
          <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
            <StatCard
              icon={FileText}
              label="Imaging Orders"
              value={totals.imaging_orders || 0}
              accent="amber"
            />
            <StatCard
              icon={Users}
              label="Referrals"
              value={totals.referrals || 0}
              accent="sky"
            />
            <StatCard
              icon={FileText}
              label="Admin Letters"
              value={totals.admin_letters || 0}
              accent="emerald"
            />
            <StatCard
              icon={Shield}
              label="Duty Profiles"
              value={totals.profiles || 0}
              accent="rose"
            />
          </div>

          {/* Footer note */}
          <p className="text-[10px] text-muted-foreground text-center pt-2 border-t border-border/30">
            Aggregate counts only · no patient-identifying data · figures exclude test entries.
            Time-saved estimates use a {stats.minutes_per_note || 7}-minute-per-note baseline derived from MTF charting telemetry.
          </p>
        </>
      )}
    </div>
  );
};

export default LiveMetricsDashboard;
